import type { ModalConfig } from "../shared/types.js";
import { useEmbedForm } from "../shared/hooks/use-embed-form.js";
import { EmbedFormInner } from "../shared/components/embed-form-inner.js";
import { ModalSkeleton } from "./modal-skeleton.js";
import { ModalSubmitting } from "./modal-submitting.js";
import { ModalSuccess } from "./modal-success.js";
import { PoweredBy } from "../shared/components/powered-by.js";

interface ModalFormProps {
  config: ModalConfig;
  containerRef: { current: HTMLElement | null };
}

export function ModalForm({ config, containerRef }: ModalFormProps) {
  const form = useEmbedForm({ config, containerRef });

  if (form.view === "loading") {
    return <ModalSkeleton />;
  }

  if (form.view === "error" && !form.embedConfig) {
    return (
      <div class="flex flex-col items-center justify-center gap-3 py-10 px-5 text-theme-muted-fg font-theme">
        <p>Unable to load form. Please try again.</p>
        <button
          type="button"
          class="px-5 py-2 border border-theme-border rounded-md bg-theme-bg cursor-pointer text-sm text-theme-fg hover:brightness-95"
          onClick={form.loadForm}
        >
          Try again
        </button>
      </div>
    );
  }

  if (form.view === "success") {
    return <ModalSuccess onResend={form.handleResend} name={form.successName} smsSent={form.smsSent} />;
  }

  // The form stays mounted while it submits, hidden behind the submitting
  // screen: a failed submit returns to the same form with every answer,
  // address pick and verification intact, not to an empty one.
  const submittingView = form.view === "submitting";

  return (
    <>
      {submittingView && <ModalSubmitting />}
      <div style={{ display: submittingView ? "none" : "contents" }}>
        {/* Header — title only */}
        <div class="px-9 py-6 font-theme">
          <h2 class="m-0 text-[24px] font-semibold leading-8 text-theme-fg">
            {form.embedConfig!.config?.title ?? "Start your free estimate"}
          </h2>
        </div>

        {/* Body — step info + fields + consent */}
        <EmbedFormInner
          embedConfig={form.embedConfig!}
          submitting={form.submitting}
          submitError={form.submitError}
          onSubmit={form.handleFormSubmit}
          onSubmitAttempt={form.clearSubmitError}
          prefill={config.prefill}
          addressSearchUrl={form.addressSearchUrl}
          formId="fv-embed-form"
          formClass="flex flex-col gap-6 px-9 py-6 border-t border-b border-theme-border font-theme"
          header={
            <div class="flex flex-col gap-2">
              <p class="m-0 text-sm font-medium text-theme-muted-fg">Step 1 of 3</p>
              <p class="m-0 text-xl font-semibold leading-none text-theme-fg">Contact details</p>
              <p class="m-0 text-sm font-normal text-theme-muted-fg leading-5">
                {form.embedConfig!.config?.description ?? "In order to start your free estimate please enter your contact details."}
              </p>
            </div>
          }
        >
          {null}
        </EmbedFormInner>

        {/* Footer — submit button */}
        <div class="px-9 py-6">
          <button
            type="submit"
            form="fv-embed-form"
            class="flex items-center justify-center gap-2 w-full h-9 px-4 border-0 rounded-3xl text-sm font-medium cursor-pointer font-[inherit] transition-[filter] duration-150 ease-out hover:not-disabled:brightness-90 disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: "var(--fv-button-bg, var(--fv-theme-primary, #0a0a0a))",
              color: "var(--fv-button-text, var(--fv-theme-primary-foreground, #fafafa))",
            }}
            disabled={form.submitting}
          >
            {form.submitting && (
              <div class="w-4 h-4 border-2 border-white/30 border-t-current rounded-full animate-[fv-spin_0.6s_linear_infinite]" />
            )}
            <span>Submit</span>
          </button>
          <PoweredBy />
        </div>
      </div>
    </>
  );
}
